import { Headphones, Image, Video, View } from 'lucide-react'; // Import icons
import { useNavigate } from 'react-router';
import { Card } from '../../../components/atoms/Card';
import { Room, RoomStatus } from '../../../types/models';
import { cn } from '../../../utils/cn';

interface RoomCardProps {
    room: Room;
    className?: string;
}

/**
 * Room card component for displaying room preview in listings
 *
 * Features:
 * - Displays room image, name, and description
 * - Shows room status indicator
 * - Clickable to navigate to room detail page
 * - Responsive design with hover effects
 */
const RoomCard = ({ room, className }: RoomCardProps) => {
    const navigate = useNavigate();

    // Get default language translation or first available
    const translation = room.translations.find((t) => t.language_code === 'en') || room.translations[0];

    // Get featured image if available
    // const featuredImage = room.media?.find((m) => m.is_featured);

    // Get image alt text from media translations or use room name
    // const imageAlt = featuredImage?.translations[0]?.alt_text || translation?.name || 'Room image';

    // Get status display properties
    const getStatusDisplay = (status: RoomStatus) => {
        switch (status) {
            case RoomStatus.Published:
                return {
                    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
                    label: 'Published',
                };
            case RoomStatus.Draft:
                return {
                    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
                    label: 'Draft',
                };
            case RoomStatus.Archived:
                return {
                    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
                    label: 'Archived',
                };
            default:
                return {
                    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200',
                    label: status,
                };
        }
    };

    const statusDisplay = getStatusDisplay(room.status);

    // Navigate to room detail page on click
    const handleClick = () => {
        navigate(`/rooms/${room.id}`);
    };

    // Truncate description to a reasonable length
    const shortDescription = translation?.description
        ? translation.description.length > 120
            ? translation.description.substring(0, 120) + '...'
            : translation.description
        : '';

    return (
        <Card
            as="article"
            className={cn('flex h-full cursor-pointer flex-col overflow-hidden', className)}
            hoverable
            variant="elevated"
            padding="none"
            onClick={handleClick}
        >
            {/* Room image */}
            <div className="relative aspect-video w-full overflow-hidden bg-gray-100 dark:bg-gray-800">
                {room.thumbnail_url ? (
                    <img
                        src={'/storage/' + room.thumbnail_url}
                        alt={'Room image'}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-600">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M4 22h16a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <path d="M3 15h6"></path>
                            <path d="M6 18V12"></path>
                        </svg>
                    </div>
                )}

                {/* Status badge */}
                {/* <div className="absolute top-2 right-2">
                    <span className={cn('rounded-full px-2 py-1 text-xs font-medium', statusDisplay.color)}>{statusDisplay.label}</span>
                </div> */}

                {/* Media Type Icons */}
                <div className="absolute top-2 right-2 flex space-x-1">
                    {/* Panorama Icon */}
                    {room.panorama_url && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Panorama">
                            <View className="h-4 w-4" />
                        </span>
                    )}
                    {/* Video Icon */}
                    {room.video_url && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Video">
                            <Video className="h-4 w-4" />
                        </span>
                    )}
                    {/* Audio Guide Icon */}
                    {(room.audio_guide_url) && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Audio Guide">
                            <Headphones className="h-4 w-4" />
                        </span>
                    )}
                    {/* Gallery/Photos Icon */}
                    {(room.gallery_urls && room.gallery_urls.length > 0) ? (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Gallery">
                            <Image className="h-4 w-4" />
                        </span>
                    ) : null}
                </div>
            </div>

            {/* Room info */}
            <div className="flex flex-1 flex-col p-4">
                <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-white">{translation?.name || 'Unnamed Room'}</h3>

                {translation?.description && <p className="mt-1 line-clamp-3 text-sm text-gray-600 dark:text-gray-300">{shortDescription}</p>}

                {/* Room details footer */}
                <div className="mt-auto flex items-center justify-between pt-3 text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex items-center">
                        {room.exhibits?.length > 0 && (
                            <span className="flex items-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="mr-1"
                                >
                                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                                    <circle cx="9" cy="9" r="2"></circle>
                                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                                </svg>
                                {room.exhibits.length} {room.exhibits.length === 1 ? 'Exhibit' : 'Exhibits'}
                            </span>
                        )}
                    </div>

                    {/* View details hint */}
                    <span className="text-primary inline-flex items-center">
                        View details
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="ml-1"
                        >
                            <polyline points="9 18 15 12 9 6"></polyline>
                        </svg>
                    </span>
                </div>
            </div>
        </Card>
    );
};

export default RoomCard;
